import React from "react";
import type { SingleValue } from "react-select";
import t from "../../../../../i18n";
import { comparatorBy } from "diagnostics-lib/comparator";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import { emptyOption } from "@ui/ReactSelect/useFormatOptionLabel";
import { getDefaultValue, multipleValue } from "../../Forms/formHelpers";
import type { ClassificationFailure } from "../../../../../api/useClassificationFailures";
import { useTranslations } from "../../../../../api/useTranslations";
import { reparabilities, REPARABILITY_COLOR } from "../../../../../api/Reparability";

// Reparability (select simple)
export const useReparabilityInput = (selectedFailures: ClassificationFailure[]) => {
  const translations = useTranslations();
  const [placeholder, setPlaceholder] = React.useState(
    t("administration.failures.table.header.reparability"),
  );

  const [isDirty, setIsDirty] = React.useState(false);

  const reparabilitiesFromSelection = selectedFailures.reduce<
    { id: string | null; name: string }[]
  >((reparabilitiesSelected, currentFailure) => {
    const currentReparability =
      "reparability" in currentFailure ? currentFailure.reparability : null;
    if (currentReparability === null) {
      return [{ id: emptyOption.value, name: "" }, ...reparabilitiesSelected];
    } else {
      return [
        ...reparabilitiesSelected,
        {
          id: currentReparability,
          name: t("reparability", currentReparability, translations),
        },
      ];
    }
  }, []);

  const defaultValue = getDefaultValue<{ id: string | null; name: string }>(
    reparabilitiesFromSelection,
    "id",
    reparability => ({
      label: reparability.name,
      value: reparability.id,
    }),
  );

  const defaultReparabilityOption = React.useMemo(() => {
    if (defaultValue?.label === "" || defaultValue?.label === multipleValue.label) {
      return null;
    }
    return defaultValue;
  }, [defaultValue]);

  const reparabilityOptions = [
    { id: emptyOption.value, name: emptyOption.label },
    ...reparabilities
      .map(value => ({
        id: String(value),
        name: t("reparability", value, translations),
        color: REPARABILITY_COLOR[value],
      }))
      .sort(comparatorBy("id")),
  ];

  const [selectedReparability, setSelectedReparability] = React.useState<SingleValue<BaseOption>>(
    defaultReparabilityOption?.value ? defaultReparabilityOption : null,
  );

  const handleReparabilityChanged = React.useCallback((option: SingleValue<BaseOption>) => {
    setSelectedReparability(option);
  }, []);

  React.useEffect(() => {
    if (selectedReparability?.value === defaultReparabilityOption?.value) {
      setIsDirty(false);
    } else {
      setIsDirty(true);
    }
  }, [selectedReparability, defaultReparabilityOption]);

  const resetValue = React.useCallback(() => {
    setSelectedReparability(defaultReparabilityOption?.value ? defaultReparabilityOption : null);
    setIsDirty(false);
  }, [defaultReparabilityOption]);

  React.useEffect(() => {
    if (defaultValue?.value === multipleValue.value)
      setPlaceholder(t("administration.modal.multiple_value"));
    else if (defaultValue === null || defaultValue.label === "") setPlaceholder(t("form.no_value"));
    else setPlaceholder(t("administration.failures.table.header.reparability"));
  }, [defaultValue]);

  return {
    placeholder,
    reparabilityOptions,
    selectedReparability,
    setSelectedReparability,
    handleReparabilityChanged,
    isDirty,
    resetValue,
  };
};
